import React, { useState } from 'react';
import { generateAffiliateLink } from '../services/cuelinks';

function PlatformResults({
  searchResults,
  searchQuery,
  onNewSearch,
  userBodyShape,
  userBodyShapeName,
  isMobile,
}) {
  const [loadingPlatform, setLoadingPlatform] = useState(null);

  const handlePlatformClick = async (platform) => {
    if (!platform.approved) return;
    setLoadingPlatform(platform.id);

    try {
      const isMobileDevice = /Android|iPhone|iPad/i.test(
        navigator.userAgent
      );

      // Myntra — force browser on mobile
      if (platform.id === 'myntra') {
        if (isMobileDevice) {
          const a = document.createElement('a');
          a.href = platform.url;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          window.open(platform.url, '_blank');
        }
        setLoadingPlatform(null);
        return;
      }

      // Ajio and Meesho — normal open
      if (
        platform.id === 'ajio' ||
        platform.id === 'meesho'
      ) {
        window.open(platform.url, '_blank');
        setLoadingPlatform(null);
        return;
      }

      // Tata CLiQ — affiliate link
      const linkData = await generateAffiliateLink(
        platform.url,
        'fitthea-user'
      );
      window.open(
        linkData.affiliateUrl || platform.url,
        '_blank'
      );

    } catch (error) {
      window.open(platform.url, '_blank');
    } finally {
      setLoadingPlatform(null);
    }
  };

  const handleColorSearch = (color) => {
    const originalQuery = searchQuery || '';
    const queryWithoutOldColor = originalQuery
      .replace(/\b(red|blue|green|yellow|pink|purple|black|white|cream|ivory|gold|maroon|navy|orange|brown|beige|grey|gray|teal|coral)\b/gi, '')
      .trim();
    const newQuery = `${queryWithoutOldColor} ${color}`.trim();
    console.log('Color search query:', newQuery);
    if (onNewSearch) onNewSearch(newQuery);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleProductClick = async (product) => {
    try {
      const linkData = await generateAffiliateLink(
        product.productUrl,
        'fitthea-user'
      );
      window.open(
        linkData.affiliateUrl || product.productUrl,
        '_blank'
      );
    } catch (error) {
      window.open(product.productUrl, '_blank');
    }
  };

  if (!searchResults) return null;

  const {
    styleNotes,
    platformResults,
    recommendedStyles,
    avoidStyles,
    sizeRecommendation,
    searchKeyword,
    filterTip,
    maxPrice,
    suggestedColors,
    suggestedColorsForOccasion,
    userMentionedColor,
    occasion,
    bodyShape,
    productData,
  } = searchResults;

  const isApifyResult = productData?.type === 'apify';
  const approvedCount = platformResults?.filter(
    p => p.approved
  ).length || 0;
  const currentBodyShape = userBodyShapeName ||
    userBodyShape || bodyShape || null;
  const showColorAlternatives = userMentionedColor &&
    suggestedColors?.length > 0;
  const showOccasionColors =
    suggestedColorsForOccasion?.length > 0;
  const affiliatePlatforms = ['tatacliq', 'myntra'];

  return (
    <div style={{
      ...styles.wrapper,
      padding: isMobile ? '0 16px 40px' : '0 40px 60px',
    }}>

      {/* AI Styling Advice */}
      {styleNotes && (
        <div style={styles.tipCard}>
          <div style={styles.tipHeader}>
            <span style={styles.tipIcon}>✦</span>
            <span style={styles.tipTitle}>
              AI Styling Advice
            </span>
          </div>

          <p style={styles.tipText}>{styleNotes}</p>

          {recommendedStyles?.length > 0 && (
            <div style={styles.tagsSection}>
              <span style={styles.tagLabel}>
                Best for you:
              </span>
              <div style={styles.tagsRow}>
                {recommendedStyles.map((s, i) => (
                  <span key={i} style={styles.tagGreen}>
                    ✓ {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {avoidStyles?.length > 0 && (
            <div style={styles.tagsSection}>
              <span style={styles.tagLabel}>Avoid:</span>
              <div style={styles.tagsRow}>
                {avoidStyles.map((s, i) => (
                  <span key={i} style={styles.tagRed}>
                    ✗ {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {filterTip && (
            <div style={styles.filterTipBox}>
              <span>💡</span>
              <span>{filterTip}</span>
            </div>
          )}
        </div>
      )}

      {/* Real Products from Apify */}
      {isApifyResult && productData?.products?.length > 0 && (
        <div style={styles.productsSection}>
          <div style={styles.productsSectionHeader}>
            <span style={styles.productsSectionTitle}>
              ✦ Real products from Myntra
            </span>
            {productData.apifyRemaining > 0 && (
              <span style={styles.apifyBadge}>
                {productData.apifyRemaining} AI searches left today
              </span>
            )}
          </div>
          <div style={{
            ...styles.productsGrid,
            gridTemplateColumns: isMobile
              ? 'repeat(2, 1fr)'
              : 'repeat(auto-fill, minmax(180px, 1fr))',
          }}>
            {productData.products.map(product => (
              <div key={product.id} style={styles.productCard}>
                <div style={styles.productImageWrapper}>
                  <img
                    src={product.image}
                    alt={product.name}
                    style={styles.productImage}
                    onError={e => {
                      e.target.src = 'https://via.placeholder.com/200x250?text=No+Image';
                    }}
                  />
                  {product.discountLabel && (
                    <div style={styles.discountBadge}>
                      {product.discountLabel}
                    </div>
                  )}
                </div>
                <div style={styles.productInfo}>
                  <div style={styles.productBrand}>
                    {product.brand}
                  </div>
                  <div style={styles.productName}>
                    {product.name}
                  </div>
                  <div style={styles.productPriceRow}>
                    <span style={styles.productPrice}>
                      Rs.{product.price?.toLocaleString()}
                    </span>
                  </div>
                  {product.bestPrice && (
                    <div style={styles.couponRow}>
                      Best Rs.{product.bestPrice?.toLocaleString()} with coupon
                    </div>
                  )}
                  {product.rating > 0 && (
                    <div style={styles.ratingRow}>
                      ⭐ {product.rating?.toFixed(1)}{' '}
                      <span style={styles.ratingCount}>
                        ({product.ratingCount?.toLocaleString()})
                      </span>
                    </div>
                  )}
                  {product.sizes && (
                    <div style={styles.sizesRow}>
                      Sizes: {product.sizes}
                    </div>
                  )}
                  <button
                    style={styles.viewProductBtn}
                    onClick={() => handleProductClick(product)}
                  >
                    View on Myntra
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Color Alternatives */}
      {showColorAlternatives && (
        <div style={styles.colorSection}>
          <div style={styles.colorHeader}>
            <span>🎨</span>
            <span style={styles.colorTitle}>
              Explore other colors
            </span>
          </div>
          <div style={styles.colorRow}>
            {suggestedColors.map((color, i) => (
              <button
                key={i}
                style={styles.colorPill}
                onClick={() => handleColorSearch(color)}
              >
                {color}
              </button>
            ))}
          </div>
          <p style={styles.colorNote}>
            Click to search this color
          </p>
        </div>
      )}

      {/* Occasion Colors */}
      {showOccasionColors && (
        <div style={styles.colorSection}>
          <div style={styles.colorHeader}>
            <span>🎨</span>
            <span style={styles.colorTitle}>
              Popular colors for{' '}
              {occasion || 'this occasion'}
            </span>
          </div>
          <div style={styles.colorRow}>
            {suggestedColorsForOccasion.map((color, i) => (
              <button
                key={i}
                style={styles.colorPill}
                onClick={() => handleColorSearch(color)}
              >
                {color}
              </button>
            ))}
          </div>
          <p style={styles.colorNote}>
            Click any color to explore more options
          </p>
        </div>
      )}

      {/* Search Info */}
      <div style={styles.searchInfo}>
        <span style={styles.searchLabel}>
          Searching for:
        </span>
        <span style={styles.searchKeyword}>
          "{searchKeyword || searchQuery}"
        </span>
        {maxPrice && (
          <span style={styles.priceTag}>
            under Rs.{maxPrice.toLocaleString()}
          </span>
        )}
        {!isApifyResult && (
          <span style={styles.searchLabel}>
            across {approvedCount} platforms
          </span>
        )}
      </div>

      {/* Platform Cards */}
      {!isApifyResult && (
        <div style={styles.platformsGrid}>
          {platformResults?.map(platform => (
            <div
              key={platform.id}
              style={{
                ...styles.platformCard,
                ...(platform.approved
                  ? styles.platformCardActive
                  : styles.platformCardInactive),
              }}
            >
              <div style={styles.platformHeader}>
                <div style={styles.platformLeft}>
                  <div style={{
                    ...styles.platformDot,
                    background: platform.approved
                      ? platform.color : '#D3D1C7',
                  }} />
                  <span style={{
                    ...styles.platformName,
                    color: platform.approved
                      ? platform.color : '#8A7F7A',
                  }}>
                    {platform.name}
                  </span>
                </div>
                <div style={styles.badgesRow}>
                  {platform.approved &&
                    affiliatePlatforms.includes(platform.id) && (
                    <span style={styles.affiliateBadge}>
                      Affiliate
                    </span>
                  )}
                  <span style={{
                    ...styles.statusBadge,
                    background: platform.approved
                      ? '#E8F5EE' : '#F5F5F5',
                    color: platform.approved
                      ? '#2E8B57' : '#8A7F7A',
                  }}>
                    {platform.approved
                      ? '✓ Live' : 'Coming Soon'}
                  </span>
                </div>
              </div>

              {platform.approved ? (
                <>
                  <p style={styles.platformDesc}>
                    Shop <strong>{searchKeyword}</strong>{' '}
                    personalised for your body type
                    {sizeRecommendation?.[platform.id] && (
                      <span style={styles.sizeTag}>
                        {' '}· Size{' '}
                        {sizeRecommendation[platform.id]}
                      </span>
                    )}
                    {maxPrice && (
                      <span style={styles.priceFilter}>
                        {' '}· under Rs.{maxPrice.toLocaleString()}
                      </span>
                    )}
                  </p>

                  {recommendedStyles?.[0] && (
                    <div style={styles.platformFilterTip}>
                      <span style={styles.filterTipEmoji}>
                        🎯
                      </span>
                      <div>
                        <span>
                          Filter by{' '}
                          <strong>{recommendedStyles[0]}</strong>{' '}
                          for your body shape
                        </span>
                        {currentBodyShape && (
                          <p style={styles.adjustNote}>
                            ✦ We refined your search based on
                            your{' '}
                            <strong>{currentBodyShape}</strong>{' '}
                            body shape. This style is more
                            flattering for your proportions
                            than what you searched for.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {maxPrice && (
                    <div style={styles.priceTip}>
                      <span>💰</span>
                      <span>
                        Sort by "Price: Low to High" to see
                        items under Rs.{maxPrice.toLocaleString()} first
                      </span>
                    </div>
                  )}

                  {/* Helpful message for Myntra on mobile */}
                  {platform.id === 'myntra' && isMobile && (
                    <div style={styles.myntraNote}>
                      <span>📱</span>
                      <span>
                        If Myntra app opens, search for{' '}
                        <strong>{searchKeyword}</strong>{' '}
                        manually in the app
                      </span>
                    </div>
                  )}

                  <button
                    style={{
                      ...styles.viewBtn,
                      background: platform.color,
                      opacity: loadingPlatform === platform.id
                        ? 0.7 : 1,
                      width: isMobile ? '100%' : 'auto',
                    }}
                    onClick={() => handlePlatformClick(platform)}
                    disabled={loadingPlatform === platform.id}
                  >
                    {loadingPlatform === platform.id
                      ? 'Opening...'
                      : `View on ${platform.name}`}
                  </button>
                </>
              ) : (
                <p style={styles.comingSoonText}>
                  We are getting approval to show results
                  from {platform.name}. Coming soon!
                </p>
              )}
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

const styles = {
  wrapper: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  tipCard: {
    background: '#F5EDD8',
    border: '1px solid #C9A96E',
    borderRadius: '16px',
    padding: '20px 24px',
    marginBottom: '16px',
  },
  tipHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '10px',
  },
  tipIcon: {
    color: '#C8715A',
    fontSize: '16px',
  },
  tipTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#9B4D3A',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  tipText: {
    fontSize: '14px',
    color: '#2C2420',
    lineHeight: '1.7',
    margin: '0 0 14px 0',
  },
  tagsSection: {
    marginBottom: '10px',
  },
  tagLabel: {
    fontSize: '11px',
    color: '#8A7F7A',
    fontWeight: '600',
    display: 'block',
    marginBottom: '6px',
  },
  tagsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  tagGreen: {
    background: '#E8F5EE',
    color: '#2E8B57',
    borderRadius: '100px',
    padding: '4px 12px',
    fontSize: '11px',
    fontWeight: '500',
  },
  tagRed: {
    background: '#FDECEA',
    color: '#D93025',
    borderRadius: '100px',
    padding: '4px 12px',
    fontSize: '11px',
    fontWeight: '500',
  },
  filterTipBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(200,113,90,0.08)',
    borderRadius: '8px',
    padding: '8px 12px',
    marginTop: '12px',
    fontSize: '12px',
    color: '#9B4D3A',
  },
  productsSection: {
    marginBottom: '24px',
  },
  productsSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  productsSectionTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#2C2420',
    fontFamily: "'Playfair Display', serif",
  },
  apifyBadge: {
    fontSize: '11px',
    background: '#E8F5EE',
    color: '#2E8B57',
    padding: '4px 12px',
    borderRadius: '100px',
    fontWeight: '600',
  },
  productsGrid: {
    display: 'grid',
    gap: '16px',
  },
  productCard: {
    background: 'white',
    border: '1px solid #EDE4DA',
    borderRadius: '14px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(44,36,32,0.06)',
  },
  productImageWrapper: {
    position: 'relative',
  },
  productImage: {
    width: '100%',
    height: '220px',
    objectFit: 'cover',
    display: 'block',
  },
  discountBadge: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    background: '#2E8B57',
    color: 'white',
    fontSize: '10px',
    fontWeight: '700',
    padding: '3px 8px',
    borderRadius: '100px',
  },
  productInfo: {
    padding: '12px',
  },
  productBrand: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#8A7F7A',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px',
  },
  productName: {
    fontSize: '12px',
    color: '#2C2420',
    lineHeight: '1.4',
    marginBottom: '8px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  productPriceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '4px',
  },
  productPrice: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#2C2420',
  },
  couponRow: {
    fontSize: '10px',
    color: '#9B4D3A',
    marginBottom: '4px',
    lineHeight: '1.4',
  },
  ratingRow: {
    fontSize: '11px',
    color: '#2C2420',
    marginBottom: '6px',
  },
  ratingCount: {
    color: '#8A7F7A',
    fontSize: '10px',
  },
  sizesRow: {
    fontSize: '10px',
    color: '#8A7F7A',
    marginBottom: '8px',
  },
  viewProductBtn: {
    width: '100%',
    background: '#FF3F6C',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '8px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  colorSection: {
    background: 'white',
    border: '1px solid #EDE4DA',
    borderRadius: '12px',
    padding: '16px 20px',
    marginBottom: '16px',
  },
  colorHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  colorTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#2C2420',
  },
  colorRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '8px',
  },
  colorPill: {
    background: '#F5F5F5',
    border: '1px solid #EDE4DA',
    borderRadius: '100px',
    padding: '6px 16px',
    fontSize: '12px',
    color: '#2C2420',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  colorNote: {
    fontSize: '11px',
    color: '#8A7F7A',
    margin: 0,
    fontStyle: 'italic',
  },
  searchInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
    fontSize: '13px',
    flexWrap: 'wrap',
  },
  searchLabel: {
    color: '#8A7F7A',
  },
  searchKeyword: {
    color: '#2C2420',
    fontWeight: '600',
    fontFamily: "'Playfair Display', serif",
    fontStyle: 'italic',
    fontSize: '15px',
  },
  priceTag: {
    background: '#E8F5EE',
    color: '#2E8B57',
    borderRadius: '100px',
    padding: '2px 10px',
    fontSize: '11px',
    fontWeight: '600',
  },
  platformsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  platformCard: {
    borderRadius: '16px',
    padding: '20px 24px',
    border: '1px solid #EDE4DA',
    transition: 'all 0.2s',
  },
  platformCardActive: {
    background: 'white',
    boxShadow: '0 2px 12px rgba(44,36,32,0.06)',
  },
  platformCardInactive: {
    background: '#FAFAF8',
  },
  platformHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '10px',
  },
  platformLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  platformDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  platformName: {
    fontSize: '16px',
    fontWeight: '700',
    fontFamily: "'Playfair Display', serif",
  },
  badgesRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  affiliateBadge: {
    fontSize: '10px',
    fontWeight: '600',
    padding: '3px 8px',
    borderRadius: '100px',
    background: '#FFF3E0',
    color: '#E65100',
  },
  statusBadge: {
    fontSize: '11px',
    fontWeight: '600',
    padding: '3px 10px',
    borderRadius: '100px',
  },
  platformDesc: {
    fontSize: '13px',
    color: '#8A7F7A',
    lineHeight: '1.6',
    margin: '0 0 12px 0',
  },
  sizeTag: {
    color: '#C8715A',
    fontWeight: '600',
  },
  priceFilter: {
    color: '#2E8B57',
    fontWeight: '500',
  },
  platformFilterTip: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    background: '#F5F5F5',
    borderRadius: '8px',
    padding: '10px 12px',
    marginBottom: '10px',
    fontSize: '12px',
    color: '#2C2420',
  },
  filterTipEmoji: {
    flexShrink: 0,
    marginTop: '1px',
  },
  adjustNote: {
    fontSize: '11px',
    color: '#9B4D3A',
    margin: '6px 0 0 0',
    lineHeight: '1.5',
    fontStyle: 'italic',
  },
  priceTip: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: '#E8F5EE',
    borderRadius: '8px',
    padding: '8px 12px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#2E8B57',
  },
  myntraNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: '#FFF3E0',
    borderRadius: '8px',
    padding: '6px 12px',
    marginBottom: '10px',
    fontSize: '11px',
    color: '#E65100',
  },
  viewBtn: {
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    padding: '10px 20px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'opacity 0.2s',
    display: 'inline-block',
  },
  comingSoonText: {
    fontSize: '12px',
    color: '#8A7F7A',
    fontStyle: 'italic',
    lineHeight: '1.5',
    margin: 0,
  },
};

export default PlatformResults;